import * as React from 'react';
import Picker from '@sdk/components/forms/Picker';
import CollapsibleSection from '@sdk/components/ui/CollapsibleSection';
import Column from '@sdk/components/ui/spacing/Column';
import gettext from '@sdk/lang/gettext';
import type { OrgSettings } from '@sdk/authentication/types/OrganisationType';

import type { OrgSettingsFields } from '../types/OrgSettingsFields';
import SettingsSection from '../../../../settings/components/SettingsSection';
import SettingsSwitcherRow from '../../../../settings/components/SettingsSwitcherRow';
import {
  getUsersSeeOtherSchedulesOption,
  getUsersSeeOtherSchedulesOptions,
} from '../../../../settings/helpers/utils';
import { VisibilitySchedulesOptions } from '../../../../settings/types';

import { useVisibilitySchedules } from './visibilitySchedules.hooks';

type Props = {
  orgLocationsCount: number;
  orgSettings: Readonly<OrgSettings>;
  orgSettingsFields: OrgSettingsFields;
};

const VisibilitySchedules: React.FC<React.PropsWithChildren<Props>> = ({
  orgSettingsFields,
  orgSettings,
  orgLocationsCount,
}) => {
  const { setUsersSeeOtherSchedulesOption } = useVisibilitySchedules();
  const {
    usersCanSeeSchedulesForOtherPositions,
    usersCanSeeSchedulesInOtherLocations,
    usersCanSeeOtherSchedules,
  } = orgSettingsFields;

  return (
    <SettingsSection withSeparator pTop={0}>
      <CollapsibleSection
        isActive={usersCanSeeOtherSchedules.value}
        headerItem={
          <SettingsSwitcherRow
            title={gettext('Visibility of schedules for employees')}
            subtitle={gettext("Allow employees to view other employees' schedules.")}
            value={usersCanSeeOtherSchedules.value}
            onChange={(value): void => usersCanSeeOtherSchedules.onChange(value)}
          />
        }
        contentItem={
          <Column pTop={10}>
            <Picker
              options={getUsersSeeOtherSchedulesOptions(
                orgLocationsCount,
                orgSettings.usersCanSeeUsersOutsideLocation
              )}
              value={getUsersSeeOtherSchedulesOption({
                orgLocationsCount,
                canSeeOtherLocations: usersCanSeeSchedulesInOtherLocations.value,
                canSeeOtherPositions: usersCanSeeSchedulesForOtherPositions.value,
              })}
              onChange={(value: VisibilitySchedulesOptions): void =>
                setUsersSeeOtherSchedulesOption({
                  value,
                  usersCanSeeSchedulesForOtherPositions,
                  usersCanSeeSchedulesInOtherLocations,
                })
              }
            />
          </Column>
        }
      />
    </SettingsSection>
  );
};

export default VisibilitySchedules;
